import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { makeStyles, mergeClasses, tokens, Button } from '@fluentui/react-components';
import { API_HOST } from '../config.js';
import { Heading, Card, Breadcrumb, Badge } from '../theme/primitives';
import { iconMap } from '../theme/icons';
import { stonePalette } from '../theme/brand';
import { formatPrice } from '../utils/format';
import carpenterVisitImg from '../assets/img/marketing/carpenter-visit.jpg';
import diyAssemblyImg from '../assets/img/marketing/diy-assembly.jpg';

const BagIcon = iconMap.shopping_bag;
const ViewInArIcon = iconMap.view_in_ar;
const GridViewIcon = iconMap.grid_view;
const EditIcon = iconMap.edit;
const DeleteIcon = iconMap.delete;
const AddIcon = iconMap.add;
const ArrowForwardIcon = iconMap.arrow_forward;
const CheckIcon = iconMap.check;

const getFurnitureLabel = (item, t) => {
  const idx = item.settings?.type ?? item.type;
  const keyMap = ['filters.wardrobes_hinged', 'filters.wardrobes_sliding', 'shelves', 'cabinets'];
  return idx != null && keyMap[idx] ? t(keyMap[idx]) : t('common.configure_btn');
};

const CATEGORY_LINKS = [
  { to: '/baldai/spintos-varstomomis-durimis', labelKey: 'wardrobes', icon: 'door_front' },
  { to: '/baldai/lentynos', labelKey: 'shelves_label', icon: 'shelves' },
  { to: '/baldai/komodos', labelKey: 'dressers_label', icon: 'night_shelter' },
  { to: '/baldai/tv-spinteles', labelKey: 'tv_label', icon: 'tv' },
];

const TRUST_ITEMS = [
  { icon: 'verified', key: 'warranty' },
  { icon: 'local_shipping', key: 'delivery_lt' },
  { icon: 'lock', key: 'secure_pay' },
];

const DIY_ITEMS = ['diy_1', 'diy_2', 'diy_3', 'diy_4'];

const FEATURE_BOXES = [
  { icon: 'verified', titleKey: 'quality_title', items: ['quality_1', 'quality_2', 'quality_3'] },
  { icon: 'sell', titleKey: 'fair_price_title', items: ['fair_1', 'fair_2', 'fair_3'] },
  { icon: 'schedule', titleKey: 'deadline_title', items: ['deadline_1', 'deadline_2', 'deadline_3'] },
];

const useStyles = makeStyles({
  page: { minHeight: '100vh', backgroundColor: stonePalette[50], display: 'flex', flexDirection: 'column' },

  // ── Empty state ──────────────────────────────────────────────────────────
  emptyBody: { flex: 1, paddingLeft: tokens.spacingHorizontalXXL, paddingRight: tokens.spacingHorizontalXXL, paddingTop: '4rem', paddingBottom: '4rem' },
  emptyInner: { maxWidth: '24rem', marginLeft: 'auto', marginRight: 'auto', textAlign: 'center' },
  // Minimalistiškesnis variantas — plokščias, be shadow "pakylėto kortelės"
  // efekto, mažesnis (žr. /admin/bugs "Perdaryk si puslapi, kad butu labiau
  // minimalistinio stiliaus").
  emptyIconChip: {
    width: '72px', height: '72px', backgroundColor: tokens.colorNeutralBackground3, borderRadius: tokens.borderRadiusCircular,
    display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: 'auto', marginRight: 'auto',
    marginBottom: tokens.spacingVerticalXL, color: tokens.colorNeutralForeground3, fontSize: '32px',
  },
  emptyTitle: { marginBottom: tokens.spacingVerticalM },
  emptyText: { marginBottom: tokens.spacingVerticalS },
  emptyCatalogText: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3, marginBottom: tokens.spacingVerticalXXL },
  emptyActions: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, marginBottom: '2.5rem' },
  emptyPopularLabel: {
    fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: tokens.spacingVerticalM,
  },
  emptyCatGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: tokens.spacingHorizontalS, textAlign: 'left' },
  // Be border/shadow — plokščia kortelė, tik subtilus fono pokytis hover
  // metu (ta pati minimalizmo kryptis, kaip emptyIconChip aukščiau).
  emptyCatCard: {
    display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalMNudge, textDecorationLine: 'none',
    padding: tokens.spacingHorizontalMNudge,
    backgroundColor: tokens.colorNeutralBackground1, borderRadius: tokens.borderRadiusXLarge,
    transitionProperty: 'background-color', transitionDuration: tokens.durationNormal,
    ':hover': { backgroundColor: tokens.colorBrandBackground2 },
  },
  emptyCatIcon: { fontSize: '18px', color: tokens.colorBrandForeground2, flexShrink: 0 },
  emptyCatLabel: {
    fontSize: tokens.fontSizeBase300, fontWeight: tokens.fontWeightMedium, color: tokens.colorNeutralForeground1,
    transitionProperty: 'color', transitionDuration: tokens.durationNormal,
  },

  // ── Buttons rendered as <Link> (route navigation, not real submit actions) ─
  btnFull: { width: '100%' },

  // ── Header title ─────────────────────────────────────────────────────────
  // Standartinis breadcrumb+title antraštės blokas — tas pats paternas, kaip
  // /baldai/:categoryid (žr. #11), pakeičia buvusį AppHeader+CheckoutSteps.
  headerBar: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  headerInner: {
    maxWidth: '80rem', marginLeft: 'auto', marginRight: 'auto',
    paddingLeft: tokens.spacingHorizontalXXL, paddingRight: tokens.spacingHorizontalXXL,
    paddingTop: tokens.spacingVerticalXL, paddingBottom: tokens.spacingVerticalXL,
    '@media (min-width: 1024px)': { paddingLeft: '3rem', paddingRight: '3rem' },
  },
  pageTitle: {
    fontSize: tokens.fontSizeBase600, lineHeight: tokens.lineHeightBase600,
    fontWeight: tokens.fontWeightBold, color: tokens.colorNeutralForeground1,
    letterSpacing: '-0.01em', margin: 0,
    '@media (min-width: 1024px)': { fontSize: tokens.fontSizeHero700, lineHeight: tokens.lineHeightHero700 },
  },

  // ── Layout ───────────────────────────────────────────────────────────────
  container: {
    maxWidth: '80rem', marginLeft: 'auto', marginRight: 'auto',
    paddingLeft: tokens.spacingHorizontalXXL, paddingRight: tokens.spacingHorizontalXXL,
    paddingTop: tokens.spacingVerticalXXL, paddingBottom: tokens.spacingVerticalXXL,
    '@media (min-width: 1024px)': { paddingLeft: '3rem', paddingRight: '3rem' },
  },
  mainGrid: {
    display: 'grid', gridTemplateColumns: '1fr', gap: tokens.spacingHorizontalXXL,
    '@media (min-width: 1024px)': { gridTemplateColumns: '2fr 1fr' },
  },

  // ── Items list ───────────────────────────────────────────────────────────
  itemsCol: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  itemRow: { display: 'flex', gap: tokens.spacingHorizontalM },
  itemThumb: {
    width: '96px', height: '80px', backgroundColor: tokens.colorNeutralBackground2, borderRadius: tokens.borderRadiusLarge,
    flexShrink: 0, overflow: 'hidden',
  },
  itemThumbImg: { width: '100%', height: '100%', objectFit: 'cover' },
  itemThumbImgContain: { width: '100%', height: '100%', objectFit: 'contain' },
  itemInfo: { flex: 1, minWidth: 0 },
  itemTopRow: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: tokens.spacingHorizontalS },
  itemLabel: { fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1, fontSize: tokens.fontSizeBase300, lineHeight: '1.2' },
  itemPrice: { fontWeight: tokens.fontWeightBold, color: tokens.colorNeutralForeground1, fontSize: tokens.fontSizeBase500, flexShrink: 0, fontVariantNumeric: 'tabular-nums' },
  itemMeta: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3, marginTop: tokens.spacingVerticalXXS },
  itemCode: {
    fontSize: tokens.fontSizeBase200, fontFamily: tokens.fontFamilyMonospace, color: tokens.colorNeutralForeground3,
    marginTop: tokens.spacingVerticalXXS, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  itemBottomRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: tokens.spacingVerticalS, flexWrap: 'wrap', gap: tokens.spacingVerticalXXS },
  itemBadges: { display: 'flex', gap: tokens.spacingHorizontalXS, flexWrap: 'wrap' },
  itemActions: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXXS },
  iconBtn: {
    width: '32px', height: '32px', minWidth: '32px', borderRadius: tokens.borderRadiusXLarge,
    display: 'flex', alignItems: 'center', justifyContent: 'center', color: tokens.colorBrandForeground2,
    backgroundColor: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
    transitionProperty: 'background-color', transitionDuration: tokens.durationNormal,
    ':hover': { backgroundColor: tokens.colorBrandBackground2 },
  },
  iconBtnDanger: {
    color: tokens.colorStatusDangerForeground2,
    ':hover': { backgroundColor: tokens.colorStatusDangerBackground1, color: tokens.colorStatusDangerForeground1 },
  },
  iconBtnSvg: { fontSize: '17px' },

  addAnotherWrap: { display: 'flex' },

  // ── Order summary ────────────────────────────────────────────────────────
  rightCol: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  summaryTitle: { fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1, marginBottom: tokens.spacingVerticalXL, display: 'block' },
  summaryLines: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, marginBottom: tokens.spacingVerticalXL },
  summaryLine: { display: 'flex', justifyContent: 'space-between', fontSize: tokens.fontSizeBase300 },
  summaryLineLabel: { color: tokens.colorNeutralForeground2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: tokens.spacingHorizontalS },
  summaryLineValue: { fontWeight: tokens.fontWeightMedium, color: tokens.colorNeutralForeground1, flexShrink: 0, fontVariantNumeric: 'tabular-nums' },

  totalBlock: { borderTop: `1px solid ${tokens.colorNeutralStroke2}`, paddingTop: tokens.spacingVerticalL, marginBottom: tokens.spacingVerticalL },
  totalRow: { display: 'flex', justifyContent: 'space-between', marginBottom: tokens.spacingVerticalXXS },
  totalLabel: { fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1 },
  totalValue: { fontWeight: tokens.fontWeightBold, fontSize: tokens.fontSizeBase500, color: tokens.colorNeutralForeground1, fontVariantNumeric: 'tabular-nums' },
  totalVat: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3, marginBottom: tokens.spacingVerticalS },

  servicesBox: {
    backgroundColor: tokens.colorNeutralBackground2, borderRadius: tokens.borderRadius3XLarge,
    padding: tokens.spacingHorizontalL, marginBottom: tokens.spacingVerticalXL,
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS,
  },
  servicesLabel: {
    fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: tokens.spacingVerticalS,
  },
  servicesRow: { display: 'flex', justifyContent: 'space-between', fontSize: tokens.fontSizeBase300 },
  servicesNote: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3, marginTop: tokens.spacingVerticalXS },

  continueBtn: { width: '100%', justifyContent: 'center', paddingTop: tokens.spacingVerticalL, paddingBottom: tokens.spacingVerticalL, fontSize: tokens.fontSizeBase400 },

  trustCard: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  trustRow: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM },
  trustIcon: { fontSize: '18px', color: tokens.colorBrandForeground2, flexShrink: 0 },
  trustLabel: { fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground2 },

  // ── Papildomos paslaugos + privalumai ────────────────────────────────────
  // #15 (antra iteracija) — vartotojas paprašė KITO išdėstymo variantо su
  // nuotraukomis, ne vienodo 5 korteliu tinklelio. Dabar: KOMPAKTIŠKA
  // pasitikėjimo juosta VIRŠUJE (3 mažos eilutės — kokybė/kaina/terminas,
  // apversta tvarka nei anksčiau), o ŽEMIAU — DU DIDELI pasirinkimo blokai
  // SU REALIOMIS NUOTRAUKOMIS (meistras vs pasidaryk pats), kurie anksčiau
  // turėjo tik ikonos ženkliuką, be jokios nuotraukos.
  bottomSection: { marginTop: '4rem', display: 'flex', flexDirection: 'column', gap: '3rem' },

  trustStrip: {
    display: 'grid', gridTemplateColumns: '1fr', gap: tokens.spacingHorizontalM,
    '@media (min-width: 768px)': { gridTemplateColumns: 'repeat(3, 1fr)' },
  },
  trustStripItem: {
    display: 'flex', alignItems: 'flex-start', gap: tokens.spacingHorizontalM,
    padding: tokens.spacingHorizontalL, backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusXLarge,
  },
  trustStripIconChip: {
    width: '40px', height: '40px', flexShrink: 0, backgroundColor: tokens.colorBrandBackground2,
    borderRadius: tokens.borderRadiusLarge, display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: tokens.colorBrandForeground2, fontSize: '18px',
  },
  trustStripTitle: { fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1, marginBottom: tokens.spacingVerticalXXS, fontSize: tokens.fontSizeBase300 },
  trustStripList: { display: 'flex', flexDirection: 'column', gap: '2px', fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },

  choiceGrid: {
    display: 'grid', gridTemplateColumns: '1fr', gap: tokens.spacingHorizontalL,
    '@media (min-width: 768px)': { gridTemplateColumns: 'repeat(2, 1fr)' },
  },
  // Paveiksliukas kairėje, turinys dešinėje (žr. /admin/bugs "padaryk kad
  // paveiksliukai butu kaireje puseje kiekvieno bloko") — be jokių dekoratyvių
  // ikonų ženkliukų, antrasis blokas standartinės pilkos (ne juodos) spalvos.
  choiceCard: {
    borderRadius: tokens.borderRadiusXLarge, overflow: 'hidden', display: 'flex', flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground1, border: `1px solid ${tokens.colorNeutralStroke2}`,
    transitionProperty: 'box-shadow, transform', transitionDuration: tokens.durationNormal,
    ':hover': { boxShadow: tokens.shadow16, transform: 'translateY(-3px)' },
    '@media (min-width: 480px)': { flexDirection: 'row' },
  },
  // Balta (ne pilka) — abu blokai turi būti tos pačios spalvos (žr. /admin/bugs
  // "antrasis turi tureti balta background color").
  choiceCardDark: { backgroundColor: tokens.colorNeutralBackground1 },
  choiceImageWrap: { width: '100%', flexShrink: 0, overflow: 'hidden', aspectRatio: '16 / 5.5', '@media (min-width: 480px)': { width: '34%', aspectRatio: 'auto' } },
  choiceImage: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  // Didesnis kairysis paddingas — tarpas nuo paveiksliuko (žr. /admin/bugs
  // "pridek padding tarp paveiksliuko ir teksto").
  choiceBody: {
    paddingTop: tokens.spacingHorizontalL, paddingBottom: tokens.spacingHorizontalL,
    paddingLeft: '1.75rem', paddingRight: tokens.spacingHorizontalL,
    display: 'flex', flexDirection: 'column', flex: 1, justifyContent: 'center', minWidth: 0,
  },

  bannerTitle: { fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1, marginBottom: tokens.spacingVerticalXXS },
  bannerText: { fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground2, marginBottom: tokens.spacingVerticalXS },
  bannerCheckList: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, marginBottom: 0, fontSize: tokens.fontSizeBase200 },
  bannerCheckRow: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS },
  bannerCheckIcon: { fontSize: '13px', color: tokens.colorBrandForeground2, flexShrink: 0 },
  bannerSamplesNote: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3, marginTop: tokens.spacingVerticalS, fontStyle: 'italic' },
  diyGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: tokens.spacingHorizontalXXS, marginTop: tokens.spacingVerticalXXS },
});

const Cart = () => {
  const [cartItems, setCartItems] = useState([]);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const s = useStyles();

  useEffect(() => { document.title = `${t('cart_page.header_title')} | Fitit`; }, [t]);

  useEffect(() => {
    setCartItems(JSON.parse(sessionStorage.getItem('furnitureCart')) || []);
  }, []);

  const handleDelete = (index) => {
    const updated = cartItems.filter((_, i) => i !== index);
    setCartItems(updated);
    sessionStorage.setItem('furnitureCart', JSON.stringify(updated));
    window.dispatchEvent(new Event('cartUpdated'));
  };

  if (cartItems.length === 0) {
    return (
      <div className={s.page}>
        <div className={s.headerBar}>
          <div className={s.headerInner}>
            <Breadcrumb items={[{ label: t('furniture_list.breadcrumb_home'), to: '/' }, { label: t('cart_page.header_title') }]} />
            <h1 className={s.pageTitle}>{t('cart_page.header_title')}</h1>
          </div>
        </div>
        <div className={s.emptyBody}>
          <div className={s.emptyInner}>
            <div className={s.emptyIconChip}>
              <BagIcon />
            </div>
            <Heading variant="h2" className={s.emptyTitle}>{t('cart_page.empty_title')}</Heading>
            <Heading variant="bodyLarge" as="p" className={s.emptyText}>{t('cart_page.empty_text')}</Heading>
            <p className={s.emptyCatalogText}>{t('cart_page.catalog_text')}</p>
            <div className={s.emptyActions}>
              <Button appearance="primary" size="large" as={Link} to="/konfiguratorius" icon={<ViewInArIcon fontSize={18} />}>
                {t('cart_page.configure_btn')}
              </Button>
              <Button appearance="outline" size="large" as={Link} to="/baldai" icon={<GridViewIcon fontSize={18} />}>
                {t('cart_page.browse_catalog')}
              </Button>
            </div>
            <p className={s.emptyPopularLabel}>{t('cart_page.popular_cats')}</p>
            <div className={s.emptyCatGrid}>
              {CATEGORY_LINKS.map(({ to, labelKey, icon }) => {
                const CatIcon = iconMap[icon];
                return (
                  <Link key={to} to={to} className={s.emptyCatCard}>
                    <CatIcon className={s.emptyCatIcon} />
                    <span className={s.emptyCatLabel}>{t(`cart_page.${labelKey}`)}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const furnitureTotal = cartItems.reduce((acc, item) => acc + (item.price || 0), 0);
  const deliveryTotal = Math.round(cartItems.reduce((acc, item) => acc + (item.services?.deliveryPrice || 0), 0));
  const carryTotal = Math.round(cartItems.reduce((acc, item) => acc + (item.services?.carryPrice || 0), 0));
  const assemblyTotal = Math.round(cartItems.reduce((acc, item) => acc + (item.services?.assemblyPrice || 0), 0));

  return (
    <div className={s.page}>

      <div className={s.headerBar}>
        <div className={s.headerInner}>
          <Breadcrumb items={[{ label: t('furniture_list.breadcrumb_home'), to: '/' }, { label: t('cart_page.header_title') }]} />
          <h1 className={s.pageTitle}>{t('cart_page.header_title')}</h1>
        </div>
      </div>

      <div className={s.container}>
        <div className={s.mainGrid}>

          {/* Items list */}
          <div className={s.itemsCol}>
            {cartItems.map((item, index) => (
              <Card key={index}>
                <div className={s.itemRow}>
                  <div className={s.itemThumb}>
                    {item.snapshot3D ? (
                      <img src={item.snapshot3D} alt={t('cart_page.item_alt')} className={s.itemThumbImg} />
                    ) : (
                      <img src={`${API_HOST}${item.imagePath}`} alt={t('cart_page.item_alt')} className={s.itemThumbImgContain} onError={e => { e.target.style.display = 'none'; }} />
                    )}
                  </div>

                  <div className={s.itemInfo}>
                    <div className={s.itemTopRow}>
                      <div className={s.itemLabel}>
                        {getFurnitureLabel(item, t)}
                      </div>
                      {item.price > 0 && (
                        <div className={s.itemPrice}>{formatPrice(item.price)}</div>
                      )}
                    </div>
                    <div className={s.itemMeta}>
                      {item.cabinets?.length || 0} {(item.cabinets?.length || 0) === 1 ? t('cart_page.section_1') : t('cart_page.section_n')}
                    </div>
                    <div className={s.itemCode}>{item.code}</div>
                    <div className={s.itemBottomRow}>
                      <div className={s.itemBadges}>
                        {item.settings?.backWall && <Badge label={t('cart_page.back_wall')} />}
                      </div>
                      <div className={s.itemActions}>
                        <button onClick={() => navigate(`/konfiguratorius/${item.code}`)} className={s.iconBtn} aria-label={t('cart_page.edit')}>
                          <EditIcon className={s.iconBtnSvg} />
                        </button>
                        <button onClick={() => handleDelete(index)} className={mergeClasses(s.iconBtn, s.iconBtnDanger)} aria-label={t('cart_page.delete')}>
                          <DeleteIcon className={s.iconBtnSvg} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))}

            <div className={s.addAnotherWrap}>
              <Button appearance="outline" as={Link} to="/konfiguratorius" icon={<AddIcon fontSize={17} />}>
                {t('cart_page.add_another')}
              </Button>
            </div>
          </div>

          {/* Order summary */}
          <div className={s.rightCol}>
            <Card padding={tokens.spacingHorizontalXXL}>
              <Heading variant="h3" as="h3" className={s.summaryTitle}>{t('cart_page.summary_title')}</Heading>
              <div className={s.summaryLines}>
                {cartItems.map((item, i) => (
                  <div key={i} className={s.summaryLine}>
                    <span className={s.summaryLineLabel}>{getFurnitureLabel(item, t)}</span>
                    <span className={s.summaryLineValue}>{item.price > 0 ? formatPrice(item.price) : '–'}</span>
                  </div>
                ))}
              </div>
              <div className={s.totalBlock}>
                <div className={s.totalRow}>
                  <span className={s.totalLabel}>{t('cart_page.furniture_total')}</span>
                  <span className={s.totalValue}>{formatPrice(furnitureTotal)}</span>
                </div>
                <p className={s.totalVat}>{t('cart_page.price_vat')}</p>
              </div>
              <div className={s.servicesBox}>
                <p className={s.servicesLabel}>{t('cart_page.extra_services')}</p>
                <div className={s.servicesRow}>
                  <span className={s.summaryLineLabel}>{t('cart_page.delivery')}</span>
                  <span className={s.summaryLineValue}>{formatPrice(deliveryTotal)}</span>
                </div>
                <div className={s.servicesRow}>
                  <span className={s.summaryLineLabel}>{t('cart_page.carrying')}</span>
                  <span className={s.summaryLineValue}>{formatPrice(carryTotal)}</span>
                </div>
                <div className={s.servicesRow}>
                  <span className={s.summaryLineLabel}>{t('cart_page.assembly')}</span>
                  <span className={s.summaryLineValue}>{formatPrice(assemblyTotal)}</span>
                </div>
                <p className={s.servicesNote}>{t('cart_page.services_note')}</p>
              </div>
              <Button appearance="primary" size="large" className={s.continueBtn} onClick={() => navigate('/checkout')}>
                {t('cart_page.continue')}
                <ArrowForwardIcon fontSize={18} />
              </Button>
            </Card>

            <Card className={s.trustCard} padding={tokens.spacingHorizontalXL}>
              {TRUST_ITEMS.map(({ icon, key }) => {
                const TrustIcon = iconMap[icon];
                return (
                  <div key={key} className={s.trustRow}>
                    <TrustIcon className={s.trustIcon} />
                    <span className={s.trustLabel}>{t(`cart_page.${key}`)}</span>
                  </div>
                );
              })}
            </Card>
          </div>
        </div>

        {/* Papildomos paslaugos + privalumai */}
        <div className={s.bottomSection}>
        {/* Kompaktiška pasitikėjimo juosta */}
        <div className={s.trustStrip}>
          {FEATURE_BOXES.map(({ icon, titleKey, items }) => {
            const FeatureIcon = iconMap[icon];
            return (
              <div key={titleKey} className={s.trustStripItem}>
                <div className={s.trustStripIconChip}>
                  <FeatureIcon />
                </div>
                <div>
                  <div className={s.trustStripTitle}>{t(`cart_page.${titleKey}`)}</div>
                  <div className={s.trustStripList}>
                    {items.map(k => <div key={k}>{t(`cart_page.${k}`)}</div>)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Du dideli pasirinkimo blokai su realiomis nuotraukomis */}
        <div className={s.choiceGrid}>
          <div className={s.choiceCard}>
            <div className={s.choiceImageWrap}>
              <img src={carpenterVisitImg} alt="Meistras montuoja baldus namuose" className={s.choiceImage} />
            </div>
            <div className={s.choiceBody}>
              <Heading variant="h3" as="h4" className={s.bannerTitle}>{t('cart_page.craftsman_title')}</Heading>
              <p className={s.bannerText}>{t('cart_page.craftsman_text')}</p>
              <div className={s.bannerCheckList}>
                <div className={s.bannerCheckRow}><CheckIcon className={s.bannerCheckIcon} />{t('cart_page.craftsman_built_in')}</div>
                <div className={s.bannerCheckRow}><CheckIcon className={s.bannerCheckIcon} />{t('cart_page.craftsman_niches')}</div>
              </div>
              <p className={s.bannerSamplesNote}>{t('cart_page.craftsman_samples')}</p>
            </div>
          </div>

          <div className={mergeClasses(s.choiceCard, s.choiceCardDark)}>
            <div className={s.choiceImageWrap}>
              <img src={diyAssemblyImg} alt="Baldo surinkimas namuose" className={s.choiceImage} />
            </div>
            <div className={s.choiceBody}>
              <Heading variant="h3" as="h4" className={s.bannerTitle}>{t('cart_page.diy_title')}</Heading>
              <p className={s.bannerText}>{t('cart_page.diy_text')}</p>
              <div className={s.diyGrid}>
                {DIY_ITEMS.map(k => (
                  <div key={k} className={s.bannerCheckRow}>
                    <CheckIcon className={s.bannerCheckIcon} />
                    {t(`cart_page.${k}`)}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
